import { useState } from 'react';
import { MessageSquareText, Save, X, Edit2, Info } from 'lucide-react';

// PER-CONTACT DOSSIER — editable. Open since 2026-07-22, unblocked 07-29 when
// Charlie shipped the `metadata` write path on PUT /campaigns/:id/contacts/:cid.
//
// Every field here is injected into the system prompt of a REAL call to a REAL
// business, so each one is labelled with what the AI actually does with it rather
// than with a generic name. `nextStep` leads because it is the "call them back
// about X" field — the reason this editor was asked for.
//
// THE FIELD LIST IS THE SERVER'S, NOT OURS. The route uses `.strict()`, so an
// unknown key is a 400 rather than a silent no-op. That is deliberate and it is
// the right call: a save that goes green and changes nothing on the call is worse
// than a rejection. Do not add an input here without a prompt consumer to match.
//
// `notes` is NOT in that list — Charlie declined it because nothing reads it. It
// is still DISPLAYED when older CSV rows carry it, but read-only and labelled as
// unused, because the card previously showed it next to fields the AI does read,
// which implied the AI uses it. It does not.

// key → { label, help, long? } — help text states the prompt consequence.
const FIELDS = [
  {
    key: 'nextStep',
    label: 'What this call is about',
    help: 'Given to the AI as its goal for the call — “YOUR GOAL / next step to drive toward”. This is the field to use for a follow-up about a specific matter.',
    long: true
  },
  {
    key: 'opener',
    label: 'Opener angle',
    help: 'Context the AI may weave in if it helps. It is NOT read verbatim — the campaign’s opening line is still what gets spoken.',
    long: true
  },
  {
    key: 'owner',
    label: 'Owner name',
    help: 'Reference only, so the AI can ANSWER if asked who it’s calling for. It never leads with this or asks to be put through to them.'
  },
  { key: 'industry', label: 'Industry', help: 'Used in the per-call briefing.' },
  { key: 'city', label: 'City', help: 'Used in the per-call briefing.' },
  { key: 'website', label: 'Website', help: 'Used in the per-call briefing.' }
];

const readValue = (metadata, key) => {
  const m = metadata && typeof metadata === 'object' ? metadata : {};
  // Tolerate the snake_case variants CSV imports produce; we always WRITE camelCase.
  if (key === 'nextStep') return m.nextStep ?? m.next_step ?? '';
  return m[key] ?? '';
};

export default function ContactDossierEditor({ contact, canEdit = false, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const start = () => {
    const d = {};
    FIELDS.forEach((f) => { d[f.key] = readValue(contact?.metadata, f.key); });
    setDraft(d);
    setError('');
    setEditing(true);
  };

  const save = async () => {
    // Send ONLY what changed. The server merges rather than replaces, so an
    // untouched key keeps whatever the CSV import put there (tier, score, the
    // FMCSA facts) — and an explicit null deletes just that one key.
    const patch = {};
    FIELDS.forEach((f) => {
      const before = String(readValue(contact?.metadata, f.key) || '');
      const after = String(draft[f.key] ?? '').trim();
      if (after === before.trim()) return;
      patch[f.key] = after === '' ? null : after;
    });
    if (Object.keys(patch).length === 0) { setEditing(false); return; }

    setBusy(true);
    setError('');
    try {
      await onSave({ metadata: patch });
      setEditing(false);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  // Legacy free-text notes from older imports. Displayed, never editable.
  const m = contact?.metadata && typeof contact.metadata === 'object' ? contact.metadata : {};
  const legacyNotes = m.notes || m.context || m.businessContext || m.business_context || m.research || '';
  const anyValue = FIELDS.some((f) => readValue(contact?.metadata, f.key));

  return (
    <div className="rounded-lg border border-stone-800 bg-stone-900/30 p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MessageSquareText size={14} className="text-stone-400" />
          <h4 className="text-sm font-medium text-stone-200">This contact — what the AI is told</h4>
        </div>
        {canEdit && !editing && (
          <button type="button" className="btn-ghost" onClick={start}>
            <Edit2 size={13} /> Edit
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-stone-500">
        Goes into the system prompt of this contact&rsquo;s next call, under &ldquo;THIS CONTACT&rdquo;.
        The platform guardrails still win over anything written here.
      </p>

      {error && (
        <p className="mb-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</p>
      )}

      {editing ? (
        <div className="space-y-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="field-label text-xs">{f.label}</label>
              {f.long ? (
                <textarea
                  className="field-input min-h-[64px] text-sm"
                  value={draft[f.key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  maxLength={2000}
                />
              ) : (
                <input
                  className="field-input text-sm"
                  value={draft[f.key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  maxLength={500}
                />
              )}
              <p className="mt-1 text-xs text-stone-500">{f.help}</p>
            </div>
          ))}
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={save} disabled={busy}>
              <Save size={13} /> {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(false)} disabled={busy}>
              <X size={13} /> Cancel
            </button>
          </div>
        </div>
      ) : anyValue ? (
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          {FIELDS.filter((f) => readValue(contact?.metadata, f.key)).map((f) => (
            <div key={f.key} className={f.long ? 'sm:col-span-2' : ''}>
              <dt className="text-xs uppercase tracking-wider text-stone-500">{f.label}</dt>
              <dd className="whitespace-pre-wrap text-stone-200">{readValue(contact?.metadata, f.key)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-sm text-stone-500">
          Nothing set for this contact{canEdit ? ' — Edit to tell the AI what this call is about.' : '.'}
        </p>
      )}

      {legacyNotes && (
        <div className="mt-3 border-t border-stone-800 pt-3">
          <div className="flex items-start gap-2">
            <Info size={13} className="mt-0.5 shrink-0 text-stone-500" />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-stone-500">
                Imported notes — not used on calls
              </div>
              <p className="mb-1 text-xs text-stone-500">
                Carried in from an import. Nothing reads this, so it does not reach the AI. Put
                anything that should affect the call in &ldquo;What this call is about&rdquo; above.
              </p>
              <div className="whitespace-pre-wrap text-sm text-stone-400">{legacyNotes}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
